import { IDeliveryNoteRow, IPdfDocItem, ISheetIndexItem, IUserRecord, ParseResult, SheetData } from './types';
import {
  DeliveryRecord,
  IDeliveryActionItem,
  IDropdownOption,
  IRawDeliveryItem,
} from '../../../../@types';



const SHEET_STATUSES = ['UPDATED', 'UNCHANGED'];

// strictStatus: for user input, the status must be exactly UPDATED or UNCHANGED (normalised to upper case)
export function parseInput(text: string, strictStatus = false): ParseResult {
  const trimmed = (text || '').trim();
  if (!trimmed || !trimmed.replace(/[\s#]/g, '')) {
    return ['Please enter sheet data starting with #'];
  }

  let rawLines: string[] = [];
  if (trimmed.includes('\n')) {
    rawLines = trimmed
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => Boolean(l.replace(/[\s#]/g, '')));
  } else if (trimmed.includes('#')) {
    rawLines = trimmed
      .split(/(?=#)/)
      .map((l) => l.trim())
      .filter((l) => Boolean(l.replace(/[\s#]/g, '')));
  } else {
    rawLines = [trimmed];
  }

  const data: SheetData[] = [];
  const errors: string[] = [];

  rawLines.forEach((chunk: string) => {
    let line = chunk.trim().replace(/;+$/, '').trim();
    if (!line || !line.replace(/[\s#]/g, '')) return;
    if (!line.startsWith('#')) {
      line = '#' + line;
    }
    const content = line.substring(1).trim();
    if (!content || !content.replace(/\s/g, '')) return;

    const parts: string[] = content
      .split('-')
      .map((x: string) => x.trim());

    const sheetNo: string = parts[0];

    if (parts.length < 4 || !parts[0] || !parts[1] || !parts[2] || !parts[3]) {
      errors.push(
        `Sheet No. ${sheetNo || ''}: Invalid format. Use #Sheet No - Sheet Title - Discipline - Status`
      );
      return;
    }

    let status = parts[parts.length - 1] as string;
    if (strictStatus) {
      const normalised = status.toUpperCase();
      if (!SHEET_STATUSES.includes(normalised)) {
        errors.push(`Sheet No. ${sheetNo}: Invalid status "${status}". Use UPDATED or UNCHANGED`);
        return;
      }
      status = normalised;
    }

    data.push({
      sheetNo,
      sheetTitle: parts.slice(1, -2).join(' - '),
      discipline: parts[parts.length - 2] as string,
      status,
    });
  });

  if (errors.length > 0) {
    return errors;
  }

  return data;
}

export const extractRevisionName = (rawRev: unknown, defaultStage: string = '-'): string => {
  if (!rawRev) return defaultStage !== undefined ? defaultStage : '-';
  if (typeof rawRev === 'object' && rawRev !== null) {
    const obj = rawRev as Record<string, unknown>;
    const pObj = (obj.payload as Record<string, unknown>) || {};
    const val = String(obj.temporaryName || obj.name || pObj.temporaryName || pObj.name || '').trim();
    if (val && !/^[0-9a-fA-F]{24}$/.test(val) && val !== 'null' && val !== 'undefined') {
      return val;
    }
    return defaultStage !== undefined ? defaultStage : '-';
  }
  const str = String(rawRev).trim();
  if (!str || str === 'null' || str === 'undefined' || /^[0-9a-fA-F]{24}$/.test(str)) {
    return defaultStage !== undefined ? defaultStage : '-';
  }
  return str;
};

export const formatCreatedBy = (rawVal: unknown, usersList: unknown[] = []): string => {
  if (!rawVal) return '-';

  if (typeof rawVal === 'object' && rawVal !== null) {
    const uObj = rawVal as Record<string, unknown>;
    const fn = String(uObj.FirstName || uObj.firstName || '').trim();
    const ln = String(uObj.LastName || uObj.lastName || '').trim();
    const fullName = `${fn} ${ln}`.trim();
    if (fullName) return fullName;

    const username = String(uObj.username || uObj.name || uObj.email || '').trim();
    if (username && !/^[0-9a-fA-F]{24}$/.test(username)) return username;

    const uId = String(uObj._id || uObj.id || '').trim();
    if (uId && Array.isArray(usersList) && usersList.length > 0) {
      const match = usersList.find((u) => {
        if (u && typeof u === 'object') {
          const item = u as Record<string, unknown>;
          return String(item._id || item.id || '') === uId;
        }
        return false;
      }) as Record<string, unknown> | undefined;

      if (match) {
        const mFn = String(match.FirstName || match.firstName || '').trim();
        const mLn = String(match.LastName || match.lastName || '').trim();
        const mFull = `${mFn} ${mLn}`.trim();
        if (mFull) return mFull;
        if (match.username) return String(match.username);
      }
    }
    return '-';
  }

  const str = String(rawVal).trim();
  if (!str || str === 'null' || str === 'undefined') return '-';

  if (/^[0-9a-fA-F]{24}$/.test(str)) {
    if (Array.isArray(usersList) && usersList.length > 0) {
      const match = usersList.find((u) => {
        if (u && typeof u === 'object') {
          const item = u as Record<string, unknown>;
          return String(item._id || item.id || '') === str;
        }
        return false;
      }) as Record<string, unknown> | undefined;

      if (match) {
        const mFn = String(match.FirstName || match.firstName || '').trim();
        const mLn = String(match.LastName || match.lastName || '').trim();
        const mFull = `${mFn} ${mLn}`.trim();
        if (mFull) return mFull;
        if (match.username) return String(match.username);
      }
    }
    return '-';
  }

  return str;
};

const toTimestamp = (value: unknown): number => {
  const ms = value ? new Date(String(value)).getTime() : 0;
  return Number.isNaN(ms) ? 0 : ms;
};

export const mapApiDeliveryNotesToRows = (
  rawNotes: unknown[],
  tasksNameList: unknown[] = [],
  usersList: unknown[] = [],
  stageName: string = ''
): IDeliveryNoteRow[] => {
  if (!Array.isArray(rawNotes) || rawNotes.length === 0) {
    return [];
  }

  const normalizeTradeName = (name: string): string => {
    const n = (name || '').trim();
    const u = n.toUpperCase();
    if (u === 'M' || u === 'MECH' || u === 'MECHANICAL') return 'Mechanical';
    if (u === 'E' || u === 'ELEC' || u === 'ELECTRICAL') return 'Electrical';
    if (u === 'P' || u === 'PLUMB' || u === 'PLUMBING') return 'Plumbing';
    if (u === 'S' || u === 'STRUCT' || u === 'STRUCTURAL') return 'Structural';
    if (u === 'FIRE' || u === 'FIRE PROTECTION') return 'Fire Protection';
    if (u === 'ARCH' || u === 'ARCHITECTURAL') return 'Architectural';
    return n || 'Mechanical';
  };

  const taskIdToTradeMap: Record<string, string> = {};
  if (Array.isArray(tasksNameList)) {
    tasksNameList.forEach((t: unknown) => {
      if (t && typeof t === 'object') {
        const obj = t as Record<string, unknown>;
        const rawName = String(
          obj.name ||
          obj.taskName ||
          (obj.taskDataId && typeof obj.taskDataId === 'object'
            ? (obj.taskDataId as Record<string, unknown>).name
            : '') ||
          obj.shortForm ||
          ''
        ).trim();
        const tradeName = rawName || normalizeTradeName(rawName);

        const ids = [
          obj._id,
          obj.id,
          obj.taskId,
          typeof obj.taskDataId === 'object' && obj.taskDataId !== null
            ? (obj.taskDataId as Record<string, unknown>)._id || (obj.taskDataId as Record<string, unknown>).id
            : obj.taskDataId,
        ]
          .filter(Boolean)
          .map(String);

        ids.forEach((id) => {
          if (id && tradeName) {
            taskIdToTradeMap[id] = tradeName;
          }
        });
      }
    });
  }

  const rows: IDeliveryNoteRow[] = [];
  let rowCounter = 1;

  rawNotes.forEach((actionItem: unknown) => {
    if (!actionItem || typeof actionItem !== 'object') return;
    const actionObj = actionItem as Record<string, unknown>;
    if (!actionObj.taskId) return;
    const rawTaskId = actionObj.taskId;
    const taskId = String(
      typeof rawTaskId === 'object' && rawTaskId !== null
        ? (rawTaskId as Record<string, unknown>)._id || (rawTaskId as Record<string, unknown>).id || ''
        : rawTaskId
    );
    const payload = (actionObj.payload as Record<string, unknown>) || {};

    let tradeName = taskId ? taskIdToTradeMap[taskId] : '';
    if (!tradeName) {
      const rawTrade = String(
        payload.trade ||
        actionObj.trade ||
        (actionObj.taskInfo as Record<string, unknown>)?.name ||
        actionObj.taskName ||
        ''
      ).trim();
      const matchedTask = Array.isArray(tasksNameList)
        ? tasksNameList.find((t) => {
          if (!t || typeof t !== 'object') return false;
          const tObj = t as Record<string, unknown>;
          const tName = String(tObj.name || tObj.taskName || '');
          const tShort = String(tObj.shortForm || '');
          return (
            tName.toLowerCase() === rawTrade.toLowerCase() ||
            (tShort && tShort.toLowerCase() === rawTrade.toLowerCase()) ||
            tName.toLowerCase().includes(rawTrade.toLowerCase()) ||
            rawTrade.toLowerCase().includes(tName.toLowerCase())
          );
        })
        : null;
      if (matchedTask && typeof matchedTask === 'object') {
        tradeName = String(
          (matchedTask as Record<string, unknown>).name ||
          (matchedTask as Record<string, unknown>).taskName ||
          rawTrade
        ).trim();
      } else {
        tradeName = rawTrade || normalizeTradeName(rawTrade);
      }
    }

    if (tradeName.toUpperCase() === 'PM') return;

    const rawActionAddedBy = actionObj.createdBy || payload.addedBy;
    const actionAddedBy = formatCreatedBy(rawActionAddedBy, usersList);

    const actionDate = actionObj.createdAt || actionObj.updatedAt || payload.date;

    const descArray = Array.isArray(payload.Description)
      ? payload.Description
      : Array.isArray(payload.descriptions)
        ? payload.descriptions
        : null;

    const itemProjectId =
      typeof actionObj.projectId === 'object' && actionObj.projectId !== null
        ? String((actionObj.projectId as Record<string, unknown>)._id || '')
        : String(actionObj.projectId || '');

    const itemStageId =
      typeof actionObj.stageId === 'object' && actionObj.stageId !== null
        ? String((actionObj.stageId as Record<string, unknown>)._id || '')
        : String(actionObj.stageId || '');

    if (descArray && descArray.length > 0) {
      descArray.forEach((descItem: unknown, dIdx: number) => {
        if (!descItem || typeof descItem !== 'object') return;
        const dObj = descItem as Record<string, unknown>;
        console.log('delivery notes', dObj);

        const noteText = String(dObj.name || dObj.text || dObj.description || dObj.comment || '');
        if (!noteText.trim()) return;

        const rawNoteAddedBy = dObj.createdBy || dObj.updatedBy || rawActionAddedBy;
        const addedBy = formatCreatedBy(rawNoteAddedBy, usersList);
        let rawDate = dObj.createdAt || dObj.updatedAt || actionDate;
        let dateStr = '';
        if (rawDate) {
          try {
            dateStr = new Date(String(rawDate)).toLocaleDateString('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            });
          } catch (e) {
            dateStr = String(rawDate);
          }
        }

        const include = dObj.toDisplay !== undefined
          ? Boolean(dObj.toDisplay)
          : (dObj.isChecked !== undefined ? Boolean(dObj.isChecked) : false);

        const rawRevOrPc =
          dObj.stageRev ||
          dObj.temporaryName ||
          dObj.stageName ||
          (typeof dObj.stageId === 'object' && dObj.stageId !== null ? dObj.stageId : null) ||
          dObj.revision ||
          dObj.revisionId ||
          dObj.planCheck ||
          dObj.planCheckId ||
          dObj.plancheck ||
          dObj.plancheckId ||
          payload.stageRev ||
          payload.temporaryName ||
          payload.stageName ||
          (typeof payload.stageId === 'object' && payload.stageId !== null ? payload.stageId : null) ||
          payload.revision ||
          payload.revisionId ||
          payload.planCheck ||
          payload.planCheckId ||
          payload.plancheck ||
          payload.plancheckId ||
          actionObj.temporaryName ||
          (typeof actionObj.stageId === 'object' && actionObj.stageId !== null ? actionObj.stageId : null) ||
          actionObj.stageName ||
          actionObj.stageRev ||
          actionObj.revision ||
          actionObj.revisionId ||
          actionObj.planCheck ||
          actionObj.planCheckId;

        const revision = extractRevisionName(rawRevOrPc, stageName);

        rows.push({
          id: String(dObj._id || dObj.id || `${actionObj._id || rowCounter}_${dIdx}`),
          actionId: String(actionObj._id || actionObj.id || ''),
          taskId,
          projectId: itemProjectId,
          stageId: itemStageId,
          revision,
          addedBy: addedBy || 'Admin',
          date: dateStr || '',
          timestamp: toTimestamp(rawDate),
          include,
          note: noteText,
          trade: tradeName,
          attachment: Array.isArray(dObj.attachment) && dObj.attachment.length > 0
            ? (dObj.attachment as unknown[]).filter((a): a is string => typeof a === 'string' && Boolean(a))
            : undefined,
          rawItem: dObj,
        });
        rowCounter++;
      });
    } else {
      const noteText = String(
        payload.note ||
        payload.notes ||
        payload.name ||
        payload.title ||
        actionObj.note ||
        actionObj.name ||
        ''
      );
      if (noteText.trim()) {
        let dateStr = '';
        if (actionDate) {
          try {
            dateStr = new Date(String(actionDate)).toLocaleDateString('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            });
          } catch (e) {
            dateStr = String(actionDate);
          }
        }

        const rawRevOrPc =
          payload.stageRev ||
          payload.temporaryName ||
          payload.stageName ||
          (typeof payload.stageId === 'object' && payload.stageId !== null ? payload.stageId : null) ||
          payload.revision ||
          payload.revisionId ||
          payload.planCheck ||
          payload.planCheckId ||
          payload.plancheck ||
          payload.plancheckId ||
          actionObj.temporaryName ||
          (typeof actionObj.stageId === 'object' && actionObj.stageId !== null ? actionObj.stageId : null) ||
          actionObj.stageName ||
          actionObj.stageRev ||
          actionObj.revision ||
          actionObj.revisionId ||
          actionObj.planCheck ||
          actionObj.planCheckId;

        const revision = extractRevisionName(rawRevOrPc, stageName);

        const include = payload.include !== undefined
          ? Boolean(payload.include)
          : (payload.toDisplay !== undefined ? Boolean(payload.toDisplay) : false);

        rows.push({
          id: String(actionObj._id || actionObj.id || rowCounter),
          actionId: String(actionObj._id || actionObj.id || ''),
          taskId,
          projectId: itemProjectId,
          stageId: itemStageId,
          revision,
          addedBy: actionAddedBy,
          date: dateStr || '',
          timestamp: toTimestamp(actionDate),
          include,
          note: noteText,
          trade: tradeName,
          attachment: Array.isArray(payload.attachment) && payload.attachment.length > 0
            ? (payload.attachment as unknown[]).filter((a): a is string => typeof a === 'string' && Boolean(a))
            : undefined,
          rawItem: { ...actionObj, ...payload },
        });
        rowCounter++;
      }
    }
  });

  return rows;
};

export const formatSheetIndexPayload = (items: ISheetIndexItem[]) => {
  return items.map((s) => {
    const sNo = s.sheetNo || '';
    const sTitle = s.sheetTitle || s.name || '';
    const sDisc = s.discipline || '';
    const sStat = s.status || 'UPDATED';
    const formattedName = (sNo || sDisc)
      ? `${sNo} - ${sTitle} - ${sDisc} - ${sStat}`
      : sTitle;

    const itemObj: Record<string, unknown> = {
      name: formattedName,
      sheetNo: sNo,
      sheetTitle: sTitle,
      discipline: sDisc,
      status: sStat,
      isUpdated: s.isUpdated !== undefined ? s.isUpdated : sStat.toLowerCase() === 'updated',
    };

    const targetId = s._id || (s.id && /^[0-9a-fA-F]{24}$/.test(s.id) ? s.id : undefined);
    if (targetId) {
      itemObj._id = targetId;
    }

    return itemObj;
  });
};

export interface TradeBadgeResult {
  name: string;
  shortForm: string;
  color: string;
}

export const getTradeBadgeInfo = (
  t: unknown,
  allTasks?: Array<Record<string, unknown>>,
  isManual: boolean = false
): TradeBadgeResult => {
  let nameStr = '';
  let shortFormStr = '';
  let colorStr = '';

  if (typeof t === 'string') {
    nameStr = t;
  } else if (t && typeof t === 'object') {
    const obj = t as Record<string, unknown>;
    nameStr = String(obj.name || obj.shortForm || obj.title || obj.trade || obj._id || '');
    shortFormStr = String(obj.shortForm || obj.shortform || '');
    colorStr = String(obj.color || '');
  }

  if (allTasks && Array.isArray(allTasks) && (nameStr.length === 24 || (typeof t === 'string' && !nameStr.includes(' ')) || !shortFormStr)) {
    const matched = allTasks.find(
      task =>
        String(task._id || task.id) === nameStr ||
        (t && typeof t === 'object' && (String(task._id || task.id) === String((t as Record<string, unknown>)._id || (t as Record<string, unknown>).taskDataId)))
    );
    if (matched) {
      nameStr = String(matched.name || matched.title || nameStr);
      if (!shortFormStr) {
        shortFormStr = String(matched.shortForm || matched.shortform || '');
      }
      if (!colorStr) {
        colorStr = String(matched.color || '');
      }
    }
  }

  if (isManual) {
    const lowerName = (nameStr + ' ' + shortFormStr).toLowerCase().trim();

    let finalShort = '';
    if (lowerName.includes('elec') || lowerName.includes('electrical')) finalShort = 'E';
    else if (lowerName.includes('mech') || lowerName.includes('mechanical')) finalShort = 'M';
    else if (lowerName.includes('plumb') || lowerName.includes('plumbing')) finalShort = 'P';
    else if (lowerName.includes('struct') || lowerName.includes('structural')) finalShort = 'S';
    else if (lowerName.includes('all standard')) finalShort = 'A';
    else if (shortFormStr && shortFormStr.length <= 3 && shortFormStr.length > 0) finalShort = shortFormStr.toUpperCase();
    else finalShort = nameStr.charAt(0).toUpperCase() || 'T';

    let finalColor = colorStr;
    if (!finalColor || finalColor === '#16a34a' || finalColor === '#000000' || finalColor === '#000') {
      if (finalShort === 'E') finalColor = '#F59E0B';
      else if (finalShort === 'M') finalColor = '#10B981';
      else if (finalShort === 'P') finalColor = '#3B82F6';
      else if (finalShort === 'S') finalColor = '#8B5CF6';
      else finalColor = '#16a34a';
    }

    return { name: nameStr, shortForm: finalShort, color: finalColor };
  }

  const finalShort = shortFormStr || (nameStr ? nameStr.charAt(0).toUpperCase() : 'T');
  let finalColor = colorStr;
  if (!finalColor) {
    finalColor = '#16a34a';
  }

  return { name: nameStr, shortForm: finalShort, color: finalColor };
};

export const formatDeliveriesData = (
  rawData: unknown[],
  currentPage: number,
  limit: number,
  stageMasterList?: Array<{ _id?: string; name?: string; color?: string }>,
  projectMasterList?: Array<{ _id?: string; name?: string; folderNumber?: string; projectManager?: { FirstName?: string; LastName?: string; name?: string; _id?: string } }>
): DeliveryRecord[] => {
  if (!Array.isArray(rawData)) return [];
  return (rawData as IRawDeliveryItem[]).map((item: IRawDeliveryItem, index: number) => {
    const rawStageId =
      typeof item?.stageId === 'object' && item?.stageId !== null
        ? String((item.stageId as { _id?: string })._id || '')
        : String(item?.stageId || '');

    const matchedStage = stageMasterList?.find(
      (s) => String(s?._id || '') === rawStageId || (s?.name && s.name === item?.name)
    );

    const stageName =
      (typeof item?.stageId === 'object' && item?.stageId !== null ? (item.stageId as { name?: string }).name : undefined) ||
      matchedStage?.name ||
      item?.name ||
      '';

    const stageColor =
      (typeof item?.stageId === 'object' && item?.stageId !== null ? (item.stageId as { color?: string }).color : undefined) ||
      matchedStage?.color ||
      '';

    const resolvedStageId =
      typeof item?.stageId === 'object' && item?.stageId !== null
        ? item.stageId
        : matchedStage
          ? { _id: rawStageId, name: matchedStage.name, color: matchedStage.color }
          : item?.stageId;

    const rawProjId =
      typeof item?.projectId === 'object' && item?.projectId !== null
        ? String((item.projectId as { _id?: string })._id || '')
        : String(item?.projectId || '');

    const matchedProject = projectMasterList?.find(
      (p) => String(p?._id || '') === rawProjId
    );

    const projectName =
      (typeof item?.projectId === 'object' && item?.projectId !== null ? (item.projectId as { name?: string }).name : undefined) ||
      matchedProject?.name ||
      '';

    const folderNumber =
      (typeof item?.projectId === 'object' && item?.projectId !== null ? (item.projectId as { folderNumber?: string }).folderNumber : undefined) ||
      matchedProject?.folderNumber ||
      '';

    const projPm =
      (typeof item?.projectId === 'object' && item?.projectId
        ? (item.projectId as { projectManager?: { FirstName?: string; LastName?: string; name?: string; _id?: string } }).projectManager
        : undefined) || matchedProject?.projectManager;

    const pmManagerObj =
      (projPm && (typeof projPm === 'string' || (projPm as any).FirstName || (projPm as any).LastName || (projPm as any)._id)
        ? projPm
        : undefined) || item?.manager;

    const pmManager = pmManagerObj
      ? typeof pmManagerObj === 'string'
        ? pmManagerObj
        : `${(pmManagerObj as any).FirstName || ''} ${(pmManagerObj as any).LastName || ''}`.trim() || (pmManagerObj as any).name || ''
      : item?.worker
        ? `${item.worker.FirstName || ''} ${item.worker.LastName || ''}`.trim()
        : '';

    const cadOperator = item?.cadOperator || '';

    const rawDate = item?.deliveryDate || '';
    const priorityStr =
      typeof item?.priority === 'boolean'
        ? (item.priority ? 'High' : 'Medium')
        : (item?.priority || 'Medium');

    const itemStageName =
      (typeof item?.stageId === 'object' && item?.stageId !== null
        ? (item.stageId as { temporaryName?: string }).temporaryName
        : undefined) ||
      item?.temporaryName ||
      stageName;
    const deliveryOptsList: IDropdownOption[] = [];
    const seenOptionKeys = new Set<string>();

    const addOption = (name: unknown) => {
      if (!name) return;
      const parsed = extractRevisionName(name, '');
      const trimmed = parsed.trim();
      if (!trimmed || trimmed === '-') return;
      const lower = trimmed.toLowerCase();
      if (!seenOptionKeys.has(lower)) {
        seenOptionKeys.add(lower);
        deliveryOptsList.push({ label: trimmed, value: trimmed });
      }
    };

    if (itemStageName) {
      addOption(itemStageName);
    }

    if (Array.isArray(item?.deliveryNotes)) {
      item.deliveryNotes.forEach((note: unknown) => {
        if (!note || typeof note !== 'object') return;
        const nObj = note as Record<string, unknown>;
        const nPayload = (nObj.payload as Record<string, unknown>) || {};
        const descriptions = Array.isArray(nPayload.Description)
          ? nPayload.Description
          : (Array.isArray(nPayload.descriptions) ? nPayload.descriptions : []);

        descriptions.forEach((desc: unknown) => {
          if (!desc || typeof desc !== 'object') return;
          const dObj = desc as Record<string, unknown>;
          addOption(dObj.revisionId || dObj.revision);
          addOption(dObj.planCheckId || dObj.planCheck || dObj.plancheckId || dObj.plancheck);
        });

        addOption(nPayload.revisionId || nPayload.revision || nObj.revisionId || nObj.revision);
        addOption(nPayload.planCheckId || nPayload.planCheck || nObj.planCheckId || nObj.planCheck);
      });
    }

    if (Array.isArray(item?.revisions)) {
      item.revisions.forEach((rev: IDeliveryActionItem) => {
        addOption(rev?.payload?.name || rev?.name || rev?.payload?.title);
      });
    }

    const planCheckList = item?.planChecks || item?.planchecks;
    if (Array.isArray(planCheckList)) {
      planCheckList.forEach((pc: IDeliveryActionItem) => {
        addOption(pc?.payload?.name || pc?.name || pc?.payload?.title);
      });
    }

    if (deliveryOptsList.length === 0) {
      addOption(item?.name || 'Full Set');
    }

    const selectedDeliveryVal =
      item?.name ||
      deliveryOptsList[deliveryOptsList.length - 1]?.value ||
      deliveryOptsList[0]?.value ||
      'Full Set';

    return {
      id: item._id || String(index + 1),
      _id: item._id,
      no: (currentPage - 1) * limit + index + 1,
      projectId: item?.projectId || (matchedProject ? { _id: rawProjId, name: projectName, folderNumber } : item?.projectId),
      stageId: resolvedStageId,
      manager: (pmManagerObj as any) || projPm,
      stageName,
      temporaryName:
        (typeof item?.stageId === 'object' && item?.stageId !== null
          ? (item.stageId as { temporaryName?: string }).temporaryName
          : undefined) ||
        item?.temporaryName ||
        undefined,
      stageColor,
      projectName,
      folderNumber,
      pmManager,
      cadOperator,
      delivery: selectedDeliveryVal,
      deliveryOptions: deliveryOptsList,
      deliveryDate: rawDate,
      status: item.status || '',
      priority: String(priorityStr),
      tasksNameList: item?.tasksNameList || [],
      publishingReady: Boolean(item.publishingReady),
      pmDelivered: Boolean(item.projectManagerDeliver),
      manualDeliver: Boolean(item.manualDeliver),
      waitingForPayment: Boolean(item.waitingForPayment),
      comments: item?.comments as unknown as string,
      sheetIndex: item?.sheetIndex,
      gdLinks: item?.gdLinks,
      uploadedDocuments: item?.uploadedDocuments,
      frontAppLinks: item?.frontAppLinks,
      deliveryNotes: item?.deliveryNotes,
    };
  });
};

export interface ParsedDeliveryRecord {
  comments: string[];
  tradeNotes: IDeliveryNoteRow[];
  gdLinks: string[];
  frontAppLinks: string[];
  sheetIndex: ISheetIndexItem[];
  pdfDocs: IPdfDocItem[];
}

export function parseDeliveryRecord(
  deliveryRecord: DeliveryRecord | null | undefined,
  rawUsers: IUserRecord[]
): ParsedDeliveryRecord {
  if (!deliveryRecord) {
    return {
      comments: [],
      tradeNotes: [],
      gdLinks: [],
      frontAppLinks: [],
      sheetIndex: [],
      pdfDocs: [],
    };
  }

  let parsedComments: string[] = [];
  if (deliveryRecord.comments) {
    if (Array.isArray(deliveryRecord.comments)) {
      parsedComments = deliveryRecord.comments
        .flatMap((c: unknown) => {
          let textStr = '';
          if (typeof c === 'string') textStr = c;
          else if (c && typeof c === 'object') {
            const obj = c as Record<string, unknown>;
            textStr = String(obj.name || obj.text || obj.comment || '');
          } else {
            textStr = String(c || '');
          }
          return textStr.split(/\n|,/).map((s) => s.trim());
        })
        .filter(Boolean);
    } else if (typeof deliveryRecord.comments === 'string') {
      parsedComments = deliveryRecord.comments
        .split(/\n|,/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }

  let parsedTradeNotes: IDeliveryNoteRow[] = [];
  if (Array.isArray(deliveryRecord.deliveryNotes) && deliveryRecord.deliveryNotes.length > 0) {
    const stageNameStr =
      deliveryRecord.temporaryName ||
      (typeof deliveryRecord.stageId === 'object' && deliveryRecord.stageId
        ? (deliveryRecord.stageId as { temporaryName?: string }).temporaryName
        : '') ||
      deliveryRecord.stageName ||
      (typeof deliveryRecord.stageId === 'object' && deliveryRecord.stageId
        ? (deliveryRecord.stageId as { name?: string }).name
        : '') ||
      '';
    parsedTradeNotes = mapApiDeliveryNotesToRows(
      deliveryRecord.deliveryNotes,
      deliveryRecord.tasksNameList,
      rawUsers,
      stageNameStr
    );
  }

  const parseLinks = (links: unknown): string[] => {
    if (!Array.isArray(links)) return [];
    return links.map((l: unknown) =>
      typeof l === 'string' ? l : (l as { link?: string })?.link || String(l)
    );
  };

  const parsedGd = parseLinks(deliveryRecord.gdLinks);
  const parsedFrontApp = parseLinks(deliveryRecord.frontAppLinks);

  let parsedSheets: ISheetIndexItem[] = [];
  if (Array.isArray(deliveryRecord.sheetIndex)) {
    let counter = 1;
    deliveryRecord.sheetIndex.forEach((s: unknown) => {
      const obj = s && typeof s === 'object' ? (s as Record<string, unknown>) : null;
      const existingMongoId = obj?._id
        ? String(obj._id)
        : obj?.id && /^[0-9a-fA-F]{24}$/.test(String(obj.id))
          ? String(obj.id)
          : undefined;
      const sNo = String(obj?.sheetNo || obj?.no || '');
      const sTitle = String(obj?.sheetTitle || obj?.name || '');
      const sDisc = String(obj?.discipline || '');
      const sStat = String(obj?.status || '');

      if (obj && (sNo || obj._id || obj.id)) {
        const formattedName = String(
          obj.name || (sNo || sDisc ? `${sNo} - ${sTitle} - ${sDisc} - ${sStat}` : sTitle)
        );
        parsedSheets.push({
          id: existingMongoId || String(counter++),
          _id: existingMongoId,
          name: formattedName,
          sheetNo: sNo,
          sheetTitle: sTitle,
          discipline: sDisc,
          status: sStat,
          isUpdated: obj.isUpdated !== undefined ? Boolean(obj.isUpdated) : sStat.toLowerCase() === 'updated',
        });
        return;
      }

      let strToParse = typeof s === 'string' ? s : String(obj?.name || obj?.sheetTitle || obj?.title || '');
      let addedFromParse = false;
      if (strToParse && strToParse.includes('-')) {
        const parsed = parseInput(strToParse);
        if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] !== 'string') {
          (parsed as SheetData[]).forEach((item) => {
            const titleStr = `${item.sheetNo} - ${item.sheetTitle} - ${item.discipline} - ${item.status}`;
            parsedSheets.push({
              id: existingMongoId || String(counter++),
              _id: existingMongoId,
              name: titleStr,
              sheetNo: item.sheetNo,
              sheetTitle: titleStr,
              discipline: item.discipline,
              status: item.status,
              isUpdated: item.status.toLowerCase() === '',
            });
          });
          addedFromParse = true;
        }
      }

      if (!addedFromParse) {
        if (typeof s === 'string') {
          parsedSheets.push({ id: String(counter++), name: s, sheetTitle: s });
        } else if (obj) {
          const fallbackTitle = String(obj.sheetTitle || obj.name || obj.title || `Sheet ${counter}`);
          parsedSheets.push({
            id: existingMongoId || String(counter++),
            _id: existingMongoId,
            name: String(obj.name || `${sNo} ${fallbackTitle}`.trim()),
            sheetNo: sNo,
            sheetTitle: fallbackTitle,
            discipline: sDisc,
            status: sStat,
            isUpdated: obj.isUpdated !== undefined ? Boolean(obj.isUpdated) : sStat.toLowerCase() === 'updated',
          });
        }
      }
    });
  }

  let parsedPdfs: IPdfDocItem[] = [];
  if (Array.isArray(deliveryRecord.uploadedDocuments)) {
    parsedPdfs = deliveryRecord.uploadedDocuments.map((doc: unknown, i: number) => {
      if (typeof doc === 'string') return { id: String(i + 1), name: doc, size: '' };
      const obj = doc as Record<string, unknown>;
      return {
        id: String(obj._id || obj.id || i + 1),
        name: String(obj.name || obj.link || obj.filename || ''),
        size: '',
      };
    });
  }

  return {
    comments: parsedComments,
    tradeNotes: parsedTradeNotes,
    gdLinks: parsedGd,
    frontAppLinks: parsedFrontApp,
    sheetIndex: parsedSheets,
    pdfDocs: parsedPdfs,
  };
}

export function buildDeliverActionPayload(params: {
  deliveryRecord: DeliveryRecord | null | undefined;
  tradeNotes: IDeliveryNoteRow[];
  commentsList: string[];
  gdLinks: string[];
  frontAppLinks: string[];
  sheetIndexes: ISheetIndexItem[];
  pdfDocs: IPdfDocItem[];
  isNewDelivery?: boolean;
}) {
  const {
    deliveryRecord,
    tradeNotes,
    commentsList,
    gdLinks,
    frontAppLinks,
    sheetIndexes,
    pdfDocs,
  } = params;

  const cleanPdfDocs = pdfDocs.map((d) => ({ link: d.name }));
  const formattedTradeNotes = tradeNotes.map((n) => ({
    ...n,
    toDisplay: Boolean(n.include),
    isChecked: Boolean(n.include),
  }));

  const projId =
    typeof deliveryRecord?.projectId === 'object' && deliveryRecord?.projectId !== null
      ? String((deliveryRecord.projectId as Record<string, unknown>)._id || '')
      : String(deliveryRecord?.projectId || '');

  const stgId = String(deliveryRecord?.id || (deliveryRecord as unknown as { _id?: string })?._id || '');

  const cleanRefObject = (obj: unknown) => {
    if (!obj || typeof obj !== 'object') return obj;
    const { projectId, stageId, taskId, ...rest } = obj as Record<string, unknown>;
    return rest;
  };

  const descriptionList = formattedTradeNotes.map((n) => {
    const raw = (n.rawItem || {}) as Record<string, unknown>;
    const rawPlanCheckId = cleanRefObject(raw.planCheckId || raw.planCheck || raw.plancheckId || raw.plancheck);
    const rawRevisionId = cleanRefObject(raw.revisionId || raw.revision);
    const rawTaskId = raw.taskId || n.taskId;
    const itemTaskId =
      typeof rawTaskId === 'object' && rawTaskId !== null
        ? String((rawTaskId as Record<string, unknown>)._id || '')
        : rawTaskId
          ? String(rawTaskId)
          : undefined;

    return {
      ...raw,
      name: n.note,
      isChecked: Boolean(n.include),
      toDisplay: Boolean(n.include),
      isInternal: false,
      comment: Array.isArray(raw.comment) ? raw.comment : [],
      stageId: raw.stageId || n.stageId || stgId,
      revisionId: rawRevisionId || null,
      planCheckId: rawPlanCheckId || null,
      taskId: itemTaskId,
      attachment: Array.isArray(raw.attachment) ? raw.attachment : [],
      isDone: raw.isDone !== undefined ? raw.isDone : false,
      createdBy: raw.createdBy || n.addedBy,
      updatedBy: raw.updatedBy || n.addedBy,
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
    };
  });

  const commentsPayload =
    commentsList.length > 0
      ? commentsList.map((c) => ({ text: c }))
      : [{ text: `Delivered ${deliveryRecord?.stageName || ''} stage drawings and documents.`.trim() }];

  const rawDeliveryDate = (deliveryRecord as { deliveryDate?: string } | null | undefined)?.deliveryDate;
  const deliveredDateValue = rawDeliveryDate ? String(rawDeliveryDate).split('T')[0] : '';

  const stageNameStr = String(
    deliveryRecord?.stageName ||
    (deliveryRecord as unknown as { name?: string })?.name ||
    deliveryRecord?.status ||
    ''
  ).toUpperCase();
  const isIprStage = stageNameStr.includes('IPR');
  const deliverActionType = isIprStage ? 'IPR Delivered' : 'Deliver';

  return {
    actionType: deliverActionType,
    projectId: projId || '',
    stageId: stgId || '',
    payload: {
      comments: commentsPayload,
      Description: descriptionList,
      gdLinks: gdLinks.map((l) => ({ link: l })),
      uploadedDocuments: cleanPdfDocs,
      sheetIndex: formatSheetIndexPayload(sheetIndexes),
      frontAppLinks: frontAppLinks.map((l) => ({ link: l })),
      readyToDeliver: true,
      deliveredDate: deliveredDateValue,
      isNewDelivery: true,
    },
  };
}
